/**
 * <complementary-sort>
 *
 * Client-side sorting + progressive "show more" for a complementary product grid.
 *
 * - Reorders the `.grid-item` children of `.complementary-grid` when the
 *   `.complementary-sort-select` value changes (pure DOM, no network request).
 * - Shows `data-batch-size` items initially; each click on `.complementary-show-more`
 *   reveals one more batch. Visibility is positional, so after a sort the visible
 *   window applies to the new order.
 *
 * Each item carries:
 *   - data-index : original metafield order (for "Par défaut")
 *   - data-price : product price in cents (numeric sort)
 *   - data-title : product title (alpha sort)
 */
class ComplementarySort extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.select = this.querySelector('.complementary-sort-select');
    this.grid = this.querySelector('.complementary-grid');
    this.button = this.querySelector('.complementary-show-more');
    if (!this.grid) return;

    this.batchSize = parseInt(this.grid.dataset.batchSize, 10) || 8;
    this.visibleCount = this.batchSize;

    if (this.select) {
      this.select.addEventListener('change', this.onSort.bind(this));
    }
    if (this.button) {
      this.button.addEventListener('click', this.onShowMore.bind(this));
    }

    this.applyVisibility();
  }

  items() {
    return Array.from(this.grid.querySelectorAll(':scope > .grid-item'));
  }

  onSort() {
    const value = this.select.value;
    const items = this.items();

    items.sort((a, b) => {
      switch (value) {
        case 'price-asc':
          return this.price(a) - this.price(b);
        case 'price-desc':
          return this.price(b) - this.price(a);
        case 'title-asc':
          return this.title(a).localeCompare(this.title(b));
        case 'title-desc':
          return this.title(b).localeCompare(this.title(a));
        case 'manual':
        default:
          return this.index(a) - this.index(b);
      }
    });

    const fragment = document.createDocumentFragment();
    items.forEach((item) => fragment.appendChild(item));
    this.grid.appendChild(fragment);

    this.applyVisibility();
  }

  onShowMore() {
    this.visibleCount += this.batchSize;
    this.applyVisibility();
  }

  applyVisibility() {
    const items = this.items();
    items.forEach((item, i) => {
      item.classList.toggle('is-hidden', i >= this.visibleCount);
    });

    if (this.button) {
      this.button.hidden = this.visibleCount >= items.length;
    }
  }

  price(el) {
    return parseInt(el.dataset.price, 10) || 0;
  }

  title(el) {
    return (el.dataset.title || '').toLowerCase();
  }

  index(el) {
    return parseInt(el.dataset.index, 10) || 0;
  }
}

if (!customElements.get('complementary-sort')) {
  customElements.define('complementary-sort', ComplementarySort);
}
